import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Plus, RefreshCw, Send, XCircle } from "lucide-react";
import { StatusPill, errorText, formatWhen } from "./common";

// Message templates used by automation "send email / send SMS" steps.
// Mirrors Outreach Approval: Master Admin drafts and submits; a Sales
// Manager or Master Administrator approves a specific version. Editing an
// approved template pauses the automations using it until re-approved.

const BLANK = { name: "", channel: "email", subject: "", body: "" };

export default function MessageTemplates({ api, showNotice, user, onChanged }) {
  const [templates, setTemplates] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [filter, setFilter] = useState("");
  const [draft, setDraft] = useState(null);
  const [rejecting, setRejecting] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get("/automation-templates", { params: { status: filter || undefined } });
      setTemplates(res.data.templates || []);
      setCanApprove(!!res.data.can_approve);
      setCanEdit(!!res.data.can_edit);
    } catch (err) {
      showNotice(errorText(err, "Failed to load templates"), "error");
    }
  }, [api, showNotice, filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMessage) => {
    try { await fn(); showNotice(okMessage, "success"); await load(); if (onChanged) onChanged(); } catch (err) { showNotice(errorText(err, "Action failed"), "error"); }
  };

  const save = () => act(async () => {
    if (draft.id) await api.put(`/automation-templates/${draft.id}`, { name: draft.name, subject: draft.channel === "email" ? draft.subject : undefined, body: draft.body });
    else await api.post("/automation-templates", { name: draft.name, channel: draft.channel, subject: draft.subject, body: draft.body });
    setDraft(null);
  }, "Template saved");

  return (
    <section className="panel glass-panel" data-testid="automation-templates">
      <div className="panel-header">
        <h3>Message templates</h3>
        <span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="template-filter">
            <option value="">All</option><option value="pending_approval">Pending approval</option><option value="draft">Draft</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
          </select>
          <button className="ghost-btn small" onClick={load}><RefreshCw size={14} /> Refresh</button>
          {canEdit && <button className="primary-btn small" data-testid="template-new" onClick={() => setDraft({ ...BLANK })}><Plus size={14} /> New template</button>}
        </span>
      </div>
      <p className="muted">Nothing is sent from a template until a Sales Manager or Master Administrator approves its current version. Placeholders: {"{{lead.clinic_name}}"}, {"{{client.clinic_name}}"}, {"{{event.x}}"}. Email footers (unsubscribe link) and SMS opt-out text are added automatically.</p>

      {draft && (
        <div className="panel" style={{ padding: 10, marginBottom: 10 }} data-testid="template-form">
          <div className="form-grid">
            <label>Name<input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
            <label>Channel<select disabled={!!draft.id} value={draft.channel} onChange={(e) => setDraft({ ...draft, channel: e.target.value })}><option value="email">Email</option><option value="sms">SMS</option></select></label>
            {draft.channel === "email" && <label>Subject<input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} /></label>}
            <label style={{ gridColumn: "1 / -1" }}>Body<textarea rows={6} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} /></label>
          </div>
          {draft.id && <p className="muted">Changing the subject or body creates a new version that needs approval again, and pauses every automation using this template.</p>}
          <button className="primary-btn" onClick={save}>Save</button> <button className="ghost-btn" onClick={() => setDraft(null)}>Cancel</button>
        </div>
      )}

      <div className="compact-list">
        {templates.map((t) => (
          <div key={t.id} className="panel" style={{ padding: 10, marginBottom: 8 }} data-testid={`template-${t.id}`}>
            <div className="compact-row">
              <StatusPill status={t.status} />
              <div>
                <strong>{t.name}</strong> <span>{t.channel.toUpperCase()} • v{t.version}{t.approved_version ? ` • approved v${t.approved_version} by ${t.approved_by || "?"} ${formatWhen(t.approved_at)}` : ""}{t.self_approved ? " (self-approved)" : ""}</span>
                {t.channel === "email" && <div>Subject: {t.subject}</div>}
                <div style={{ whiteSpace: "pre-wrap" }}>{t.body}</div>
                {t.rejection_reason && t.status === "rejected" && <div style={{ color: "#f87171" }}>Rejected: {t.rejection_reason}</div>}
                {!!t.used_by?.length && <div className="muted">Used by: {t.used_by.map((a) => `${a.name}${a.enabled ? "" : " (off)"}`).join(", ")}</div>}
              </div>
            </div>
            <div className="compact-row">
              {canEdit && ["draft", "approved", "rejected", "pending_approval"].includes(t.status) && <button className="ghost-btn small" onClick={() => setDraft({ id: t.id, name: t.name, channel: t.channel, subject: t.subject, body: t.body })}>Edit</button>}
              {canEdit && ["draft", "rejected"].includes(t.status) && <button className="ghost-btn small" data-testid={`template-submit-${t.id}`} onClick={() => act(() => api.post(`/automation-templates/${t.id}/submit`), "Submitted for approval")}><Send size={14} /> Submit for approval</button>}
              {canApprove && t.status === "pending_approval" && (
                <>
                  <button className="primary-btn small" data-testid={`template-approve-${t.id}`} onClick={() => act(() => api.post(`/automation-templates/${t.id}/approve`, { version: t.version }), "Template approved")}><CheckCircle2 size={14} /> Approve v{t.version}</button>
                  <input placeholder="Reason for rejecting" value={rejecting[t.id] || ""} onChange={(e) => setRejecting({ ...rejecting, [t.id]: e.target.value })} />
                  <button className="ghost-btn small" disabled={!(rejecting[t.id] || "").trim()} onClick={() => act(() => api.post(`/automation-templates/${t.id}/reject`, { reason: rejecting[t.id] }), "Template rejected")}><XCircle size={14} /> Reject</button>
                </>
              )}
            </div>
          </div>
        ))}
        {!templates.length && <div className="empty-cell">No templates{filter ? " in this state" : " yet"}.</div>}
      </div>
    </section>
  );
}
